import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { ShippingError } from '@wix/cashier-express-checkout-widget/dist/src/types/Shipping';
import { SPECS } from '../SPECS';
import { CheckoutService } from './CheckoutService';
import { getPaymentAmount } from '../../utils/getPaymentAmount';
import { getDiscountsToAlignWith } from '../../utils/discounts.utils';
import { getOrderItemsFromCheckout } from '../../utils/getOrderItemsFromCheckout';
import { getPaymentBreakdown } from '../../utils/getPaymentBreakdown';
import { shouldNavigateToFastFlowCheckout, shouldNavigateToFastFlowCheckoutV1 } from '../../utils/shouldNavigateToFastFlow';
import { NavigationService } from './NavigationService';
import { confirmPayment } from '@wix/cashier-express-checkout-widget/dist/src/utils/confirmPayment';
import { trackInitiateCheckout } from '../../utils/analytics.utils';
import { mapToCashierShippingAddress } from '../../utils/addresses.utils';
import { CART_V2_PREFIX } from '../../utils/consts';
export class ExpressCheckoutService {
  constructor(_ref) {
    let {
      expressCheckoutSettings,
      controllerConfig,
      biService,
      getCurrency,
      locale,
      httpClient,
      errorHandler,
      continueShoppingUrl,
      successUrl
    } = _ref;
    _defineProperty(this, "checkoutService", void 0);
    _defineProperty(this, "navigationService", void 0);
    _defineProperty(this, "expressCheckoutSettings", void 0);
    _defineProperty(this, "controllerConfig", void 0);
    _defineProperty(this, "biService", void 0);
    /** @deprecated */
    _defineProperty(this, "cartId", '');
    _defineProperty(this, "getInitialPaymentData", async checkoutId => {
      const expressModel = await this.checkoutService.getCheckoutByCheckoutId(checkoutId);
      this.controllerConfig.wixCodeApi.window.trackEvent(...trackInitiateCheckout({
        expressModel
      }));
      this.cartId = expressModel.cartId;
      const extraData = {
        priceVerificationToken: expressModel.priceVerificationToken
      };
      return {
        paymentData: {
          ...this.calculatePaymentData(expressModel)
        },
        extraData
      };
    });
    _defineProperty(this, "onShippingContactSelected", async (checkoutId, shippingContact) => {
      try {
        const {
          countryCode,
          city,
          state,
          postalCode
        } = mapToCashierShippingAddress(shippingContact);
        const address = {
          countryCode,
          city,
          postalCode,
          state
        };
        const {
          expressModel,
          shippingMethods
        } = await this.checkoutService.updateDeliveryAddress(checkoutId, address);

        /* istanbul ignore if */
        if (shippingMethods && (!expressModel.deliveryMethodCode || shippingMethods.length === 0)) {
          return {
            paymentData: {
              error: ShippingError.SHIPPING_ADDRESS_UNSERVICEABLE
            }
          };
        }
        return {
          paymentData: {
            ...this.calculatePaymentData(expressModel),
            shippingMethods
          },
          extraData: {
            priceVerificationToken: expressModel.priceVerificationToken
          }
        };
      } catch {
        return {
          paymentData: {
            error: ShippingError.SHIPPING_ADDRESS_UNSERVICEABLE
          }
        };
      }
    });
    _defineProperty(this, "onShippingMethodSelected", async (checkoutId, shippingMethod) => {
      const expressModel = await this.checkoutService.setDeliveryMethod(checkoutId, shippingMethod.identifier);
      return {
        paymentData: {
          ...this.calculatePaymentData(expressModel)
        },
        extraData: {
          priceVerificationToken: expressModel.priceVerificationToken
        }
      };
    });
    _defineProperty(this, "calculatePaymentData", expressModel => {
      const discountsToAlignWith = getDiscountsToAlignWith(expressModel);
      return {
        orderItems: getOrderItemsFromCheckout(expressModel, discountsToAlignWith),
        paymentBreakdown: getPaymentBreakdown(expressModel, discountsToAlignWith),
        paymentAmount: getPaymentAmount(expressModel)
      };
    });
    _defineProperty(this, "onPaymentAuthorized", async (checkoutId, paymentAuthorizedArgs, _ref2, _ref3) => {
      let {
        shouldUpdateShippingAddress,
        wasShippingMethodSelected,
        latestTotalAmount,
        priceVerificationToken
      } = _ref2;
      let {
        contentAppId,
        contentComponentId,
        widgetId
      } = _ref3;
      if (paymentAuthorizedArgs.error) {
        return {
          result: 'error'
        };
      }
      this.biService.clickContinueEWalletModalSf(checkoutId, this.cartId ?? undefined);
      if (!this.useCartDirectlyFromV2(checkoutId)) {
        return this.onPaymentAuthorizedV1(checkoutId, paymentAuthorizedArgs, {
          shouldUpdateShippingAddress,
          wasShippingMethodSelected,
          latestTotalAmount
        }, {
          contentAppId,
          contentComponentId,
          widgetId
        });
      }
      let updateResult;
      try {
        updateResult = await this.checkoutService.updateDeliveryAndBillingInfo(checkoutId, paymentAuthorizedArgs.billingContact, mapToCashierShippingAddress(paymentAuthorizedArgs.shippingContact, paymentAuthorizedArgs.billingContact), {
          shouldUpdateShippingAddress
        });
      } catch {
        return {
          result: 'error'
        };
      }
      if (await shouldNavigateToFastFlowCheckout({
        lineItems: updateResult.lineItems,
        checkoutId,
        purchaseFlowId: updateResult.purchaseFlowId,
        expressCheckoutSettings: this.expressCheckoutSettings,
        checkoutService: this.checkoutService,
        biService: this.biService,
        isMemberLoggedIn: this.controllerConfig.wixCodeApi.user.currentUser.loggedIn
      })) {
        await this.navigationService.navigateToCheckout(checkoutId, paymentAuthorizedArgs.detailsId);
        return {
          result: 'success'
        };
      }
      const redirectUrls = await this.getRedirectUrls(checkoutId, paymentAuthorizedArgs.detailsId, contentAppId, contentComponentId);
      let placeOrderResult;
      try {
        const placeOrderParams = {
          id: checkoutId,
          paymentToken: paymentAuthorizedArgs.detailsId,
          redirectUrls,
          priceVerificationToken
        };
        placeOrderResult = await this.checkoutService.placeOrder(placeOrderParams);
      } catch {
        await this.navigationService.navigateToCheckout(checkoutId, paymentAuthorizedArgs.detailsId);
        return {
          result: 'success'
        };
      }
      if (await this.isPaymentConfirmed({
        price: latestTotalAmount,
        paymentResponseToken: placeOrderResult.paymentResponseToken,
        widgetId
      })) {
        await this.navigationService.navigateToThankYouPage(placeOrderResult.orderId);
      }
      return {
        result: 'success'
      };
    });
    // eslint-disable-next-line sonarjs/cognitive-complexity
    _defineProperty(this, "onPaymentAuthorizedV1", async (checkoutId, _ref4, _ref5, _ref6) => {
      let {
        detailsId,
        shippingContact,
        billingContact
      } = _ref4;
      let {
        shouldUpdateShippingAddress,
        wasShippingMethodSelected,
        latestTotalAmount
      } = _ref5;
      let {
        contentAppId,
        contentComponentId,
        widgetId
      } = _ref6;
      let expressModel;
      const isMemberLoggedIn = this.controllerConfig.wixCodeApi.user.currentUser.loggedIn;
      let shouldRedirectToCollectShipping = false;
      let hasShippingZipCodeError = false;
      let hasShippingSubdivisionError = false;
      try {
        const result = await this.checkoutService.updateDeliveryAndBillingInfoV1(checkoutId, billingContact, mapToCashierShippingAddress(shippingContact, billingContact), {
          shouldUpdateShippingAddress,
          wasShippingMethodSelected
        });
        expressModel = result.expressModel;
        shouldRedirectToCollectShipping = result.shouldRedirectToCollectShipping;
        hasShippingZipCodeError = result.hasShippingZipCodeError;
        hasShippingSubdivisionError = result.hasShippingSubdivisionError;
      } catch {
        return {
          result: 'error'
        };
      }
      if (await shouldNavigateToFastFlowCheckoutV1({
        expressModel,
        prevTotalAmount: latestTotalAmount,
        expressCheckoutSettings: this.expressCheckoutSettings,
        checkoutService: this.checkoutService,
        biService: this.biService,
        isMemberLoggedIn,
        shouldRedirectToCollectShipping,
        hasShippingZipCodeError,
        hasShippingSubdivisionError
      })) {
        await this.navigationService.navigateToCheckout(checkoutId, detailsId);
      } else {
        const redirectUrls = await this.getRedirectUrls(checkoutId, detailsId, contentAppId, contentComponentId);
        const {
          orderId,
          paymentResponseToken
        } = await this.checkoutService.placeOrderV1({
          id: checkoutId,
          paymentToken: detailsId,
          redirectUrls
        });
        if (!orderId) {
          return {
            result: 'error'
          };
        }
        if (await this.isPaymentConfirmed({
          price: expressModel.payNow.amount,
          paymentResponseToken,
          widgetId
        })) {
          await this.navigationService.navigateToThankYouPage(orderId);
        }
      }
      return {
        result: 'success'
      };
    });
    _defineProperty(this, "isExpressSupported", () => {
      var _this$expressCheckout;
      return Boolean((_this$expressCheckout = this.expressCheckoutSettings) == null ? void 0 : _this$expressCheckout.isExpressSupported);
    });
    _defineProperty(this, "useCartDirectlyFromV2", checkoutId => {
      return this.controllerConfig.essentials.experiments.enabled(SPECS.CallCartV2FromExpressCheckout) && checkoutId.startsWith(CART_V2_PREFIX);
    });
    _defineProperty(this, "getRedirectUrls", async (checkoutId, detailsId, contentAppId, contentComponentId) => {
      const thankYouPageUrl = await this.navigationService.getThankYouPageUrlForPlaceOrder();
      return {
        errorUrl: await this.navigationService.getCheckoutErrorUrl(checkoutId, detailsId, contentAppId, contentComponentId),
        cancelUrl: await this.navigationService.getCheckoutCancelUrl(checkoutId, detailsId, contentAppId, contentComponentId),
        successUrl: thankYouPageUrl,
        pendingUrl: thankYouPageUrl
      };
    });
    _defineProperty(this, "isPaymentConfirmed", async _ref7 => {
      var _result;
      let {
        price,
        paymentResponseToken,
        widgetId
      } = _ref7;
      let result;
      if (Number(price) > 0) {
        var _this$controllerConfi, _this$controllerConfi2, _this$controllerConfi3;
        result = await confirmPayment({
          chargeResponseToken: paymentResponseToken,
          meta: {
            appDefId: this.controllerConfig.appParams.appDefinitionId,
            msid: (_this$controllerConfi = this.controllerConfig.platformAPIs.bi) == null ? void 0 : _this$controllerConfi.metaSiteId,
            visitorId: (_this$controllerConfi2 = this.controllerConfig.platformAPIs) == null || (_this$controllerConfi2 = _this$controllerConfi2.bi) == null ? void 0 : _this$controllerConfi2.visitorId,
            widgetId: widgetId
          },
          // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
          biLoggerFactory: (_this$controllerConfi3 = this.controllerConfig.platformAPIs) == null ? void 0 : _this$controllerConfi3.biLoggerFactory,
          redirectFn: this.controllerConfig.wixCodeApi.location.to
        });
      }
      return ((_result = result) == null ? void 0 : _result.clientStatusV2) !== 'InProcess';
    });
    this.checkoutService = new CheckoutService({
      httpClient,
      errorHandler,
      controllerConfig,
      getCurrency,
      locale
    });
    this.navigationService = new NavigationService(controllerConfig, {
      continueShoppingUrl,
      successUrl
    });
    this.expressCheckoutSettings = expressCheckoutSettings;
    this.controllerConfig = controllerConfig;
    this.biService = biService;
  }
}
//# sourceMappingURL=ExpressCheckoutService.js.map